import * as React from 'react'
import { FC, useCallback, useMemo } from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { useSelector } from 'react-redux'

import { currencyString } from '@sales-center/common'
import { defaultFilters } from '@user/store/opportunity/filters'
import { ProjectedSalesVisualization } from './ProjectedSalesVisualization'
import { selectAllSalesFunnelStages } from '@user/store/salesFunnel/slice'
import { selectSalesFunnelFilters } from '@user/store/salesFunnelSummary/slice'
import { useNavigate } from 'react-router-dom'
import { useSaveOpportunityFilters } from '@user/store/opportunity/hooks'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  ISoldServiceStatusSummary,
  SoldServiceStatusSummaryPopover,
} from '@user/components/SoldServices'
import { useSetOppSoldServicesStatusFilter } from '@user/components/SalesFunnel'
import { InfoIcon, LightTooltip } from '@sales-center/components'

interface Props {
  closedWon: number
  handleClose: () => void
  open: number
  percentageComplete: number
  projected: number
  salesFunnelId?: number
  salesFunnelName?: string
  showTooltip?: boolean
  soldServicesSummary: ISoldServiceStatusSummary[]
  type: 'detail' | 'summary'
}

const getStyles = (props: Props, theme: Theme) => ({
  subTitle: {
    fontSize: '12px',
    marginBottom: theme.spacing(0.5),
  },

  title: {
    fontFamily: 'Source Sans Pro Semibold',
    fontSize: props.type == 'summary' ? '16px' : '14px',
  },

  total: {
    fontFamily: 'Source Sans Pro',
    fontSize: props.type == 'summary' ? '24px' : '21px',
  },

  value: {
    fontFamily: 'Source Sans Pro Semibold',
    marginLeft: theme.spacing(0.5),
  },

  valueLink: {
    color: '#005AA3',
    fontFamily: 'Source Sans Pro Semibold',
    marginLeft: theme.spacing(0.5),
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  viewStatusBreakdown: {
    marginLeft: theme.spacing(-0.8),
  },
})

export const ProjectedSales: FC<Props> = (props) => {
  const {
    closedWon,
    handleClose,
    open,
    percentageComplete,
    projected,
    salesFunnelId,
    salesFunnelName,
    showTooltip,
    soldServicesSummary,
    type,
  } = props

  const navigate = useNavigate()

  const saveOpportunityFilters = useSaveOpportunityFilters()
  const allStages = useSelector(selectAllSalesFunnelStages)
  const funnelFilters = useSelector(selectSalesFunnelFilters)

  const closedWonStageId = useMemo(() => {
    const stage = allStages.find((s) => {
      return s.salesFunnelId == salesFunnelId && s.name == 'Closed/Won'
    })

    return stage?.id
  }, [allStages, salesFunnelId])

  const goToExpectedOpps = useCallback(() => {
    saveOpportunityFilters({
      ...defaultFilters,
      filters: {
        ...defaultFilters.filters,
        closed: {
          ...defaultFilters.filters.closed,
          value: 'null',
        },
        expectedClosingDate: {
          ...defaultFilters.filters.expectedClosingDate,
          value: funnelFilters.filters.createdDate.value,
        },
        salesFunnelId: {
          ...defaultFilters.filters.salesFunnelId,
          value:
            salesFunnelId && salesFunnelName
              ? {
                  mode: 'any',
                  options: [{ label: salesFunnelName, value: salesFunnelId }],
                }
              : defaultFilters.filters.salesFunnelId.value,
        },
        assignee: {
          ...defaultFilters.filters.assignee,
          value: funnelFilters.filters.ownerIds.value,
        },
        salesTeamId: {
          ...defaultFilters.filters.salesTeamId,
          value: funnelFilters.filters.salesTeamIds.value,
        },
      },
    })

    navigate('/opportunities')
    handleClose()
  }, [
    funnelFilters,
    handleClose,
    navigate,
    salesFunnelId,
    salesFunnelName,
    saveOpportunityFilters,
  ])

  const goToClosedWonOpps = useCallback(() => {
    saveOpportunityFilters({
      ...defaultFilters,
      filters: {
        ...defaultFilters.filters,
        closed: {
          ...defaultFilters.filters.closed,
          value: 'notnull',
        },
        closedDate: {
          ...defaultFilters.filters.closedDate,
          value: funnelFilters.filters.createdDate.value,
        },
        salesFunnelId: {
          ...defaultFilters.filters.salesFunnelId,
          value:
            salesFunnelId && salesFunnelName
              ? {
                  mode: 'any',
                  options: [{ label: salesFunnelName, value: salesFunnelId }],
                }
              : defaultFilters.filters.salesFunnelId.value,
        },
        assignee: {
          ...defaultFilters.filters.assignee,
          value: funnelFilters.filters.ownerIds.value,
        },
        salesFunnelStageId: {
          ...defaultFilters.filters.salesFunnelStageId,
          value: !!closedWonStageId
            ? {
                mode: 'any',
                options: [{ label: 'Closed/Won', value: closedWonStageId }],
              }
            : defaultFilters.filters.salesFunnelStageId.value,
        },
        salesTeamId: {
          ...defaultFilters.filters.salesTeamId,
          value: funnelFilters.filters.salesTeamIds.value,
        },
      },
    })

    navigate('/opportunities')
    handleClose()
  }, [
    closedWonStageId,
    funnelFilters,
    handleClose,
    navigate,
    salesFunnelId,
    salesFunnelName,
    saveOpportunityFilters,
  ])

  const [setFilter] = useSetOppSoldServicesStatusFilter()

  const handleManageClick = useCallback(
    (status) => {
      const { salesFunnelIds, salesTeamIds, createdDate, ownerIds, type } =
        funnelFilters.filters
      setFilter(
        status,
        salesFunnelId && salesFunnelName
          ? {
              mode: 'any',
              options: [{ label: salesFunnelName, value: salesFunnelId }],
            }
          : salesFunnelIds.value,
        salesFunnelName,
        salesTeamIds.value,
        createdDate.value,
        ownerIds.value,
        type.value,
      )
      handleClose()
      navigate('/opportunities')
    },
    [
      funnelFilters.filters.salesFunnelIds.value,
      funnelFilters.filters.salesTeamIds.value,
      funnelFilters.filters.createdDate.value,
      funnelFilters.filters.ownerIds.value,
      funnelFilters.filters.type.value,
      setFilter,
      salesFunnelName,
      handleClose,
      navigate,
    ],
  )

  const theme = useTheme()
  const styles = getStyles(props, theme)
  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" alignItems="center">
        <Box>
          <Typography sx={styles.title}>Projected Sales</Typography>
          {type == 'summary' && (
            <Typography sx={styles.subTitle}>
              These are sales that have already closed and are expected to close
              within the selected date range.
            </Typography>
          )}
        </Box>

        <Box display="flex" flexGrow={1} justifyContent="flex-end">
          <Typography sx={styles.total}>{`$${currencyString(
            projected || 0,
          )}`}</Typography>
        </Box>
      </Box>
      <ProjectedSalesVisualization
        percentageComplete={percentageComplete > 100 ? 100 : percentageComplete}
        type={type}
      />
      <Box display="flex">
        <Typography onClick={type == 'detail' ? goToClosedWonOpps : () => {}}>
          Closed/Won
          <span
            style={type == 'detail' ? styles.valueLink : styles.value}
          >{`$${currencyString(closedWon || 0)}`}</span>
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          flexGrow={1}
          justifyContent="flex-end"
        >
          {showTooltip && (
            <LightTooltip title="This is calculated by looking at the expected closing dates of all open opportunities within the selected date range and their total values.">
              <Box
                width="15px"
                height="15px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                ml={0.2}
              >
                <InfoIcon height="11px" width="11px" />
              </Box>
            </LightTooltip>
          )}
          <Typography onClick={goToExpectedOpps}>
            Expected
            <span style={styles.valueLink}>{`$${currencyString(
              open || 0,
            )}`}</span>
          </Typography>
        </Box>
      </Box>
      <Box sx={styles.viewStatusBreakdown} display="flex">
        <SoldServiceStatusSummaryPopover
          onManageClick={handleManageClick}
          summaries={soldServicesSummary}
        />
      </Box>
    </Box>
  )
}

ProjectedSales.displayName = 'ProjectedSales'
